import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { FileSpecificationService } from 'src/app/services/file-specifications/file-specification.service';
import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';

import { StillsFileSpecificationCreateDialogComponent } from './file-specifications/stills-file-specification-create-dialog/stills-file-specification-create-dialog.component';
import { StillsFileSpecificationUpdateDialogComponent } from './file-specifications/stills-file-specification-update-dialog/stills-file-specification-update-dialog.component';
import { VideosFileSpecificationCreateDialogComponent } from './file-specifications/videos-file-specification-create-dialog/videos-file-specification-create-dialog.component';
import { VideosFileSpecificationUpdateDialogComponent } from './file-specifications/videos-file-specification-update-dialog/videos-file-specification-update-dialog.component';
import { FileSpecificationDeleteDialogComponent } from './file-specifications/file-specification-delete-dialog.component';
import { RangeFilenameConventionCreateDialogComponent } from './filename-conventions/range-filename-convention-create-dialog/range-filename-convention-create-dialog.component';
import { RangeFilenameConventionUpdateDialogComponent } from './filename-conventions/range-filename-convention-update-dialog/range-filename-convention-update-dialog.component';
import { EvFilenameConventionCreateDialogComponent } from './filename-conventions/ev-filename-convention-create-dialog/ev-filename-convention-create-dialog.component';
import { EvFilenameConventionUpdateDialogComponent } from './filename-conventions/ev-filename-convention-update-dialog/ev-filename-convention-update-dialog.component';
import { FilenameConventionDeleteDialogComponent } from './filename-conventions/filename-convention-delete-dialog.component';

export type FileSpecificationEditorMedia = 'stills' | 'videos';
export type FilenameConventionCreateFamily = 'range' | 'ev';
import { OutputFormulaRangeCreateDialogComponent } from './output-formulas/output-formula-range-create-dialog.component';
import { OutputFormulaEvCreateDialogComponent } from './output-formulas/output-formula-ev-create-dialog.component';
import { OutputFormulaRangeUpdateDialogComponent } from './output-formulas/output-formula-range-update-dialog.component';
import { OutputFormulaEvUpdateDialogComponent } from './output-formulas/output-formula-ev-update-dialog.component';
import { OutputFormulaDeleteDialogComponent } from './output-formulas/output-formula-delete-dialog.component';
import { outputFormulaValueIsRangeFamily } from 'src/app/models/output-formulas/output-formula-value';
import { OutputCompositionHistoryDialogComponent } from './output-composition-history-dialog.component';

export type OutputCompositionTab = 'filename-conventions' | 'file-specifications' | 'output-formulas';

const TAB_ORDER: readonly OutputCompositionTab[] = [
  'filename-conventions',
  'file-specifications',
  'output-formulas',
];

@Component({
  selector: 'app-output-composition-card',
  templateUrl: './output-composition-card.component.html',
  styleUrls: ['./output-composition-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputCompositionCardComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() organizationId: string | null = null;

  fileSpecifications: FileSpecification[] = [];
  filenameConventions: FilenameConvention[] = [];
  outputFormulas: OutputFormula[] = [];

  fileSpecificationsLoaded = false;
  filenameConventionsLoaded = false;
  outputFormulasLoaded = false;

  readonly tabOrder: readonly OutputCompositionTab[] = TAB_ORDER;
  readonly defaultTabIndex = TAB_ORDER.indexOf('output-formulas');

  constructor(
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly fileSpecificationService: FileSpecificationService,
    private readonly filenameConventionService: FilenameConventionService,
    private readonly outputFormulaService: OutputFormulaService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.hasAnyPermission) return;
    this.loadOutputFormulas();
    if (this.canReadFileSpecifications) this.loadFileSpecifications();
    if (this.canReadFilenameConventions) this.loadFilenameConventions();
  }

  get hasAnyPermission(): boolean {
    return (
      this.canReadFileSpecifications ||
      this.canReadFilenameConventions ||
      this.canReadOutputFormulas
    );
  }

  get canReadFileSpecifications(): boolean {
    return (
      this.authService.hasPermission(Permission.FileSpecificationsRead) ||
      this.authService.hasPermission(Permission.FileSpecificationsFull)
    );
  }

  get canWriteFileSpecifications(): boolean {
    return this.authService.hasPermission(Permission.FileSpecificationsFull);
  }

  get canAuditFileSpecifications(): boolean {
    return this.authService.hasPermission(Permission.FileSpecificationsAudit);
  }

  get canReadFilenameConventions(): boolean {
    return (
      this.authService.hasPermission(Permission.FilenameConventionsRead) ||
      this.authService.hasPermission(Permission.FilenameConventionsFull)
    );
  }

  get canWriteFilenameConventions(): boolean {
    return this.authService.hasPermission(Permission.FilenameConventionsFull);
  }

  get canAuditFilenameConventions(): boolean {
    return this.authService.hasPermission(Permission.FilenameConventionsAudit);
  }

  get canReadOutputFormulas(): boolean {
    return (
      this.authService.hasPermission(Permission.OutputFormulasRead) ||
      this.authService.hasPermission(Permission.OutputFormulasFull)
    );
  }

  get canWriteOutputFormulas(): boolean {
    return this.authService.hasPermission(Permission.OutputFormulasFull);
  }

  get canAuditOutputFormulas(): boolean {
    return this.authService.hasPermission(Permission.OutputFormulasAudit);
  }

  onTabChanged(index: number): void {
    const tab = TAB_ORDER[index];
    if (tab === 'filename-conventions' && !this.filenameConventionsLoaded) this.loadFilenameConventions();
    if (tab === 'file-specifications' && !this.fileSpecificationsLoaded) this.loadFileSpecifications();
    if (tab === 'output-formulas' && !this.outputFormulasLoaded) this.loadOutputFormulas();
  }

  openCreateFileSpecificationDialog(media: FileSpecificationEditorMedia): void {
    if (this.organizationId === null) return;
    const ref =
      media === 'stills'
        ? this.dialog.open(StillsFileSpecificationCreateDialogComponent, {
            data: { organizationId: this.organizationId },
          })
        : this.dialog.open(VideosFileSpecificationCreateDialogComponent, {
            data: { organizationId: this.organizationId },
          });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: FileSpecification | undefined) => {
        if (result) this.loadFileSpecifications();
      });
  }

  openUpdateFileSpecificationDialog(row: FileSpecification): void {
    if (this.organizationId === null) return;
    const ref =
      row.value.settings.media === 'stills'
        ? this.dialog.open(StillsFileSpecificationUpdateDialogComponent, {
            data: { organizationId: this.organizationId, current: row },
          })
        : this.dialog.open(VideosFileSpecificationUpdateDialogComponent, {
            data: { organizationId: this.organizationId, current: row },
          });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: FileSpecification | undefined) => {
        if (result) this.loadFileSpecifications();
      });
  }

  openDeleteFileSpecificationDialog(row: FileSpecification): void {
    if (this.organizationId === null) return;
    this.dialog
      .open(FileSpecificationDeleteDialogComponent, {
        data: { organizationId: this.organizationId, current: row },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean | undefined) => {
        if (confirmed === true) this.loadFileSpecifications();
      });
  }

  openFileSpecificationHistoryDialog(row: FileSpecification): void {
    if (this.organizationId === null) return;
    this.dialog.open(OutputCompositionHistoryDialogComponent, {
      data: {
        organizationId: this.organizationId,
        entityKind: 'file-specification',
        id: row.id,
        name: row.name,
        media: row.value.settings.media,
      },
    });
  }

  openCreateFilenameConventionDialog(family: FilenameConventionCreateFamily): void {
    if (this.organizationId === null) return;
    if (family === 'range') {
      this.dialog
        .open(RangeFilenameConventionCreateDialogComponent, {
          data: { organizationId: this.organizationId },
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: FilenameConvention | undefined) => {
          if (result) this.loadFilenameConventions();
        });
      return;
    }
    this.dialog
      .open(EvFilenameConventionCreateDialogComponent, {
        data: { organizationId: this.organizationId },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: FilenameConvention | undefined) => {
        if (result) this.loadFilenameConventions();
      });
  }

  openUpdateFilenameConventionDialog(row: FilenameConvention): void {
    if (this.organizationId === null) return;
    if (row.value.payload.mode === 'rangeBased') {
      this.dialog
        .open(RangeFilenameConventionUpdateDialogComponent, {
          data: { organizationId: this.organizationId, current: row },
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: FilenameConvention | undefined) => {
          if (result) this.loadFilenameConventions();
        });
      return;
    }
    this.dialog
      .open(EvFilenameConventionUpdateDialogComponent, {
        data: { organizationId: this.organizationId, current: row },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: FilenameConvention | undefined) => {
        if (result) this.loadFilenameConventions();
      });
  }

  openDeleteFilenameConventionDialog(row: FilenameConvention): void {
    if (this.organizationId === null) return;
    this.dialog
      .open(FilenameConventionDeleteDialogComponent, {
        data: { organizationId: this.organizationId, current: row },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean | undefined) => {
        if (confirmed === true) this.loadFilenameConventions();
      });
  }

  openFilenameConventionHistoryDialog(row: FilenameConvention): void {
    if (this.organizationId === null) return;
    this.dialog.open(OutputCompositionHistoryDialogComponent, {
      data: {
        organizationId: this.organizationId,
        entityKind: 'filename-convention',
        id: row.id,
        name: row.name,
        mode: row.value.payload.mode,
      },
    });
  }

  openCreateRangeOutputFormulaDialog(): void {
    if (this.organizationId === null) return;
    this.dialog
      .open(OutputFormulaRangeCreateDialogComponent, {
        data: {
          organizationId: this.organizationId,
          fileSpecifications: this.fileSpecifications,
          filenameConventions: this.filenameConventions,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: OutputFormula | undefined) => {
        if (result) this.loadOutputFormulas();
      });
  }

  openCreateEvOutputFormulaDialog(): void {
    if (this.organizationId === null) return;
    this.dialog
      .open(OutputFormulaEvCreateDialogComponent, {
        data: {
          organizationId: this.organizationId,
          fileSpecifications: this.fileSpecifications,
          filenameConventions: this.filenameConventions,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: OutputFormula | undefined) => {
        if (result) this.loadOutputFormulas();
      });
  }

  openUpdateOutputFormulaDialog(row: OutputFormula): void {
    if (this.organizationId === null) return;
    const data = {
      organizationId: this.organizationId,
      current: row,
      fileSpecifications: this.fileSpecifications,
      filenameConventions: this.filenameConventions,
    };
    const afterClosed$ = outputFormulaValueIsRangeFamily(row.value)
      ? this.dialog.open(OutputFormulaRangeUpdateDialogComponent, { data }).afterClosed()
      : this.dialog.open(OutputFormulaEvUpdateDialogComponent, { data }).afterClosed();
    afterClosed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: OutputFormula | undefined) => {
        if (result) this.loadOutputFormulas();
      });
  }

  openDeleteOutputFormulaDialog(row: OutputFormula): void {
    if (this.organizationId === null) return;
    this.dialog
      .open(OutputFormulaDeleteDialogComponent, {
        data: { organizationId: this.organizationId, current: row },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean | undefined) => {
        if (confirmed === true) this.loadOutputFormulas();
      });
  }

  openOutputFormulaHistoryDialog(row: OutputFormula): void {
    if (this.organizationId === null) return;
    this.dialog.open(OutputCompositionHistoryDialogComponent, {
      data: {
        organizationId: this.organizationId,
        entityKind: 'output-formula',
        id: row.id,
        name: row.name,
      },
    });
  }

  private loadFileSpecifications(): void {
    if (this.organizationId === null) return;
    this.fileSpecificationService
      .list(this.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows: FileSpecification[]) => {
        this.fileSpecifications = rows;
        this.fileSpecificationsLoaded = true;
        this.cdr.markForCheck();
      });
  }

  private loadFilenameConventions(): void {
    if (this.organizationId === null) return;
    this.filenameConventionService
      .list(this.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows: FilenameConvention[]) => {
        this.filenameConventions = rows;
        this.filenameConventionsLoaded = true;
        this.cdr.markForCheck();
      });
  }

  private loadOutputFormulas(): void {
    if (this.organizationId === null) return;
    this.outputFormulaService
      .list(this.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows: OutputFormula[]) => {
        this.outputFormulas = rows;
        this.outputFormulasLoaded = true;
        this.cdr.markForCheck();
      });
  }
}
